import { isDefined, type Maybe, noop } from "@banjoanton/utils";
import {
    createContext,
    type PropsWithChildren,
    useContext,
    useMemo,
    useState,
} from "react";
import { Progress } from "../components/ui/progress";
import { cn } from "@/lib/utils";

export const ProgressLoader = ({
    value,
    className,
}: {
    value: number;
    className?: string;
}) => (
    <div className={cn(className)}>
        <Progress value={value} />
    </div>
);

export const Spinner = ({ radius = "3rem" }: { radius?: string }) => (
    <div
        className="inline-block animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
        style={{ height: radius, width: radius }}
        role="status"
    />
);

type LoadingProps = {
    isLoading: boolean;
    text?: string;
    progress?: number;
};

export const FullScreenLoading = ({
    isLoading,
    text,
    progress,
}: LoadingProps) => {
    if (!isLoading) {
        return null;
    }

    const render = () => {
        if (isDefined(progress)) {
            return (
                <ProgressLoader className="w-1/2 max-w-md" value={progress} />
            );
        }
        return <Spinner />;
    };

    return (
        <div className="fixed left-0 top-0 z-50 flex h-full w-full flex-col items-center justify-center gap-4 backdrop-blur-lg">
            {render()}
            <span>{text}</span>
        </div>
    );
};

export type LoadingType = {
    isLoading: boolean;
    setLoading: (options: LoadingProps) => void;
    progress?: number;
};

const LoadingContext = createContext<LoadingType>({
    isLoading: false,
    setLoading: noop,
    progress: undefined,
});

export const useLoading = () => useContext(LoadingContext);

type GlobalLoadingProviderProps = PropsWithChildren;

export const GlobalLoadingProvider = ({
    children,
}: GlobalLoadingProviderProps) => {
    const [isLoadingGlobal, setIsLoadingGlobal] = useState(false);
    const [textGlobal, setTextGlobal] = useState<Maybe<string>>();
    const [progressGlobal, setProgressGlobal] = useState<number | undefined>(
        undefined
    );

    const setLoading = ({ isLoading, text, progress }: LoadingProps) => {
        if (isLoading === false) {
            setProgressGlobal(undefined);
            setTextGlobal(undefined);
            setIsLoadingGlobal(false);
            return;
        }

        if (isDefined(progress)) {
            setProgressGlobal(progress);
        } else {
            setProgressGlobal(undefined);
        }

        if (isDefined(text)) {
            setTextGlobal(text);
        }

        setIsLoadingGlobal(isLoading);
    };

    const value = useMemo(
        () => ({
            isLoading: isLoadingGlobal,
            setLoading,
            progress: progressGlobal,
        }),
        [isLoadingGlobal, progressGlobal]
    );

    return (
        <LoadingContext.Provider value={value}>
            <FullScreenLoading
                isLoading={isLoadingGlobal}
                text={textGlobal}
                progress={progressGlobal}
            />
            {children}
        </LoadingContext.Provider>
    );
};
